import React, { useState, useEffect } from 'react';
import { Search, ZoomIn, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { getGallery } from '../api';

const Gallery = () => {
  const { t, i18n } = useTranslation();
  const [galleryItems, setGalleryItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const galleryData = await getGallery();
        setGalleryItems(galleryData);
      } catch (err) {
        setError(t('gallery.error'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredItems = galleryItems.filter((item) => {
    const title = i18n.language === 'en' ? (item.title_en || item.title) : item.title;
    const description = i18n.language === 'en' ? (item.description_en || item.description) : item.description;
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <section id="gallery" className="py-24 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-12 h-12 animate-spin text-pink-500" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="gallery" className="py-24 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-white to-orange-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-fuchsia-600 via-pink-600 to-orange-500 bg-clip-text text-transparent">
              {t('gallery.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {t('gallery.subtitle')}
          </p>
        </div>

        {/* Search Bar ONLY - NO CATEGORIES */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder={t('gallery.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-6 rounded-full border-2 focus:border-orange-500"
            />
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedImage(item)}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={item.image.startsWith('/api/') 
                    ? `${process.env.REACT_APP_BACKEND_URL}${item.image}` 
                    : item.image}
                  alt={i18n.language === 'en' ? (item.title_en || item.title) : item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <h3 className="text-white text-xl font-bold mb-2">
                  {i18n.language === 'en' ? (item.title_en || item.title) : item.title}
                </h3>
                <p className="text-white/90 text-sm mb-4">
                  {i18n.language === 'en' ? (item.description_en || item.description) : item.description}
                </p>
                <div className="flex items-center gap-2">
                  <ZoomIn className="w-5 h-5 text-white" />
                  <span className="text-white text-sm font-medium">{t('gallery.viewDetail')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredItems.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">
              {t('gallery.noResults')}
            </p>
          </div>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl w-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white text-4xl hover:text-pink-500 transition-colors"
              >
                ×
              </button>
              <img
                src={selectedImage.image.startsWith('/api/') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${selectedImage.image}` 
                  : selectedImage.image}
                alt={i18n.language === 'en' ? (selectedImage.title_en || selectedImage.title) : selectedImage.title}
                className="w-full rounded-2xl shadow-2xl"
              />
              <div className="bg-white rounded-2xl p-6 mt-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {i18n.language === 'en' ? (selectedImage.title_en || selectedImage.title) : selectedImage.title}
                </h3>
                <p className="text-gray-600">
                  {i18n.language === 'en' ? (selectedImage.description_en || selectedImage.description) : selectedImage.description}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
